// event-registration-form-app/components/FormField.jsx

import React, { useEffect } from "react";
import _ from "lodash";

const FormField = ({ form, field, readOnly }) => {
  const fieldTypeClass = `c-event-registration-form__field-${_.toLower(field.type)}`;

  // return <pre>{JSON.stringify(field, null, 2)}</pre>;

  // Build a key for the field
  let key = `f-${field.id}`;

  // Error path determination
  const errorPath = field.id;

  // For single-value fields (text, dropdown, single checkbox)
  const updateValue = (value) => {
    let fieldPath = field.id; // Normally this would be the direct name
    form.setFieldValue(fieldPath, value); // Update Formik state
    form.setFieldTouched(fieldPath, true, false); // Mark as touched
    form.validateField(fieldPath); // Validate the field
  };

  const getFieldValue = () => {
    let fieldPath = field.id;
    return _.get(form.values, fieldPath, "");
  };

  // For fields with multiple options (Checkboxes, PurchasableItems)
  const updateGroupValue = (optionId, checked) => {
    let fieldPath = field.id;
    let currentFieldValue = _.get(form.values, fieldPath, []);
    let updatedValue = currentFieldValue;

    if (checked) {
      updatedValue = currentFieldValue.includes(optionId) ? currentFieldValue : [...currentFieldValue, optionId];
    } else {
      updatedValue = currentFieldValue.filter((id) => id !== optionId);
    }

    form.setFieldValue(fieldPath, updatedValue);
    form.setFieldTouched(errorPath, true, false);
  };

  const optionIsSelected = (optionId) => {
    // Assuming `field.id` is correctly formed as "attendees[x].#"
    let currentFieldValue = _.get(form.values, field.id, []);
    return currentFieldValue.includes(optionId);
  };

  return (
    <div className={["c-event-registration-form__field", fieldTypeClass, field.columns == 1 ? "" : "c-event-registration-form__field--full-width"].join(" ")} key={key}>
      <label className="c-event-registration-form__label" htmlFor={field.id}>
        {field.niceName || field.label}
        {field.required == 1 && <span className="c-event-registration-form__required-indicator">*</span>}
      </label>
      <div className="c-event-registration-form__input-wrapper">
        {readOnly ? (
          <ReferenceView form={form} field={field} componentKey={key} getFieldValue={getFieldValue} optionIsSelected={optionIsSelected} />
        ) : (
        <InputView
            form={form}
            field={field}
            componentKey={key}
            getFieldValue={getFieldValue}
            updateValue={updateValue}
            updateGroupValue={updateGroupValue}
            optionIsSelected={optionIsSelected}
          />
        )}
      </div>
      {field.description && (
        <label className="c-event-registration-form__description" htmlFor={field.id}>
          {field.description}
        </label>
      )}
      {_.get(form.errors, errorPath) && <div style={{ color: "red", fontWeight: "bold" }}>{_.get(form.errors, errorPath)}</div>}
    </div>
  );
};

const ReferenceView = ({ form, field, componentKey, getFieldValue, optionIsSelected }) => {
  return (
    <div className="c-event-registration-form__reference-wrapper">
      {(field.type === "Text" || field.type === "Textarea") && <div>{getFieldValue()}</div>}
      {field.type === "Checkbox" && <div>{getFieldValue() ? "Checked" : "Unchecked"}</div>}
      {field.type === "Dropdown" && <div>{field.id == "gClub" ? field.options.find((option) => option.id == getFieldValue()).name : getFieldValue()}</div>}
      {field.type === "PurchasableItems" && (
        <div>
          {field.purchasableOptions.map((option) => (
            <div key={`${componentKey}-${option.id}`}>{optionIsSelected(option.id) && `${option.name} - $${option.cost}`}</div>
          ))}
        </div>
      )}
      {field.type === "Checkboxes" && (
        <div>
          {field.options.map((option, index) => (
            <div key={`${componentKey}-${option.id}`}>{optionIsSelected(option.id) && `${option.name}`}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const InputView = ({ form, field, componentKey, getFieldValue, updateValue, updateGroupValue, optionIsSelected }) => {
  // Precheck any required purchasable options
  useEffect(() => {
    if (field.type === "PurchasableItems") {
      field.purchasableOptions.forEach((option) => {
        if (option.fixedCost) {
          updateGroupValue(option.id, true);
        }
      });
    }
  }, []);

  return (
    <>
      {field.type === "Text" && (
        <input
          name={field.id}
          type="text"
          value={getFieldValue()}
          required={field.required}
          onChange={(e) => updateValue(e.target.value)}
          onBlur={(e) => form.setFieldTouched(field.id, true, false)}
        />
      )}
      {field.type === "Textarea" && (
        <textarea
          style={{ height: "150px" }}
          name={field.id}
          value={getFieldValue()}
          required={field.required}
          onChange={(e) => updateValue(e.target.value)}
          onBlur={(e) => form.setFieldTouched(field.id, true, false)}
        />
      )}
      {field.type === "Checkbox" && (
        <input
          type="checkbox"
          id={field.id}
          checked={getFieldValue()}
          required={field.required}
          onChange={(e) => updateValue(e.target.checked)}
          onBlur={(e) => form.setFieldTouched(field.id, true, false)}
        />
      )}
      {field.type === "Dropdown" && (
        <select
          name={field.id}
          value={getFieldValue()}
          required={field.required}
          onChange={(e) => updateValue(e.target.value)}
          onBlur={(e) => form.setFieldTouched(field.id, true, false)}
        >
          {field.options.map((option) => (
            <option key={`${componentKey}-${option.id}`} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      )}
      {field.type === "PurchasableItems" && (
        <div>
          {field.purchasableOptions.map((option) => (
            <div key={`${componentKey}-${option.id}`} className="c-event-registration-form__input-purchasable-item-wrapper">
              <input
                type="checkbox"
                id={`${field.id}-${option.id}`}
                name={field.id}
                checked={option.fixedCost || optionIsSelected(option.id)}
                required={field.required}
                onChange={(e) => {
                  if (option.fixedCost) {
                    console.error("fixedCost");
                    updateGroupValue(option.id, true);
                  } else {
                    updateGroupValue(option.id, e.target.checked);
                  }
                }}
                onBlur={(e) => form.setFieldTouched(field.id, true, false)}
                disabled={option.fixedCost}
              />
              <label htmlFor={`${field.id}-${option.id}`}>
                {option.name} - <strong>${option.cost}</strong> {option.fixedCost ? " (Compulsory fee)" : ""}
              </label>
            </div>
          ))}
        </div>
      )}
      {field.type === "Checkboxes" && (
        <div>
          {field.options.map((option, index) => (
            <div key={`${componentKey}-${option.id}`} className="c-event-registration-form__input-checkbox-wrapper">
              <input
                type="checkbox"
                id={`${field.id}-${option.id}`}
                name={`attendees[${index}].${field.id}`}
                value={option.name}
                checked={optionIsSelected(option.id)}
                onChange={(e) => updateGroupValue(option.id, e.target.checked)}
                onBlur={(e) => form.setFieldTouched(field.id, true, false)}
              />
              <label htmlFor={`${field.id}-${option.id}`}>{option.name}</label>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FormField;
